import React from 'react';
import { howItWorksSteps } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { UserPlus, Code, Rocket, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const iconMap = {
  UserPlus: UserPlus,
  Code: Code,
  Rocket: Rocket
};

export const HowItWorks = () => {
  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Get Started in
            <span className="block mt-2 bg-gradient-to-r from-purple-600 via-pink-500 to-blue-600 bg-clip-text text-transparent">
              3 Simple Steps
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From signup to Voice AI in minutes. No complex setup, no steep learning curve. 
            Just intelligent voice experiences powered by OpenAI.
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="space-y-16">
            {howItWorksSteps.map((step, index) => {
              const Icon = iconMap[step.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div key={step.id} className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-12`}>
                  {/* Icon/Number Side */}
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="w-32 h-32 bg-gradient-to-br from-purple-600 via-pink-600 to-blue-600 rounded-2xl flex items-center justify-center shadow-2xl">
                        <Icon className="text-white" size={48} />
                      </div>
                      <div className="absolute -top-4 -right-4 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center text-2xl font-bold text-gray-900 shadow-lg">
                        {step.id}
                      </div>
                    </div>
                  </div>
                  
                  {/* Content Side */}
                  <div className="flex-1">
                    <Card className="border-2 hover:shadow-xl transition-all">
                      <CardContent className="p-8">
                        <h3 className="text-3xl font-bold text-gray-900 mb-4">{step.title}</h3>
                        <p className="text-lg text-gray-600 leading-relaxed">{step.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Process */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">What happens next?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-2">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4 text-purple-600 font-bold text-lg">
                  1
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Quick Onboarding</h3>
                <p className="text-gray-600">Interactive setup guide to configure your Voice AI preferences in under 5 minutes.</p>
              </CardContent>
            </Card>
            
            <Card className="border-2">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center mb-4 text-pink-600 font-bold text-lg">
                  2
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Get API Keys</h3>
                <p className="text-gray-600">Receive your authentication credentials instantly to start making API calls.</p>
              </CardContent>
            </Card>
            
            <Card className="border-2">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4 text-blue-600 font-bold text-lg">
                  3
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Test Integration</h3>
                <p className="text-gray-600">Use our sandbox environment to test voice interactions before going live.</p>
              </CardContent>
            </Card>
            
            <Card className="border-2">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4 text-purple-600 font-bold text-lg">
                  4
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Go Live</h3>
                <p className="text-gray-600">Deploy your Voice AI and start delivering intelligent voice experiences.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Ready to get started?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Join businesses using Nyhive to deliver intelligent Voice AI experiences to their customers.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-8 py-6 text-lg">
              Start Your Free Trial
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};